import {
  isOwnerOperatingDashboardReadModel,
  type OwnerDashboardDrillTarget,
  type OwnerDashboardStatus,
  type OwnerOperatingDashboardReadModel,
} from "@billiard/contracts";
import { ApiRequestError, requestOperatorJson } from "./api";

export interface OwnerDashboardOperatorContext {
  tenantId: string;
  storeId: string;
}

export type OwnerOperatingDashboardQueryKey = readonly [
  "owner-operating-dashboard",
  string,
  string,
  string,
];

export interface OwnerDashboardStatusDisplay {
  label: "正常" | "需要关注" | "需要立即处理" | "暂不可用";
  tone: "positive" | "warning" | "critical" | "unavailable";
}

const drillHrefByTarget: Record<OwnerDashboardDrillTarget, string> = {
  DUTY: "/tasks",
  SHIFTS: "/shifts",
  TABLES: "/frontdesk",
  ORDERS: "/orders",
  REVIEW: "/review",
  INVENTORY: "/products",
};

const statusDisplayByStatus: Record<OwnerDashboardStatus, OwnerDashboardStatusDisplay> = {
  GREEN: { label: "正常", tone: "positive" },
  YELLOW: { label: "需要关注", tone: "warning" },
  RED: { label: "需要立即处理", tone: "critical" },
  UNAVAILABLE: { label: "暂不可用", tone: "unavailable" },
};

function isCanonicalDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(value)) return false;
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    Number.isFinite(milliseconds) && new Date(milliseconds).toISOString().slice(0, 10) === value
  );
}

function operatorContextIsCanonical(context: OwnerDashboardOperatorContext): boolean {
  return [context.tenantId, context.storeId].every(
    (value) => value.length >= 1 && value.length <= 128 && value.trim() === value,
  );
}

export function ownerOperatingDashboardQueryKey(
  context: OwnerDashboardOperatorContext,
  businessDate: string,
): OwnerOperatingDashboardQueryKey {
  return ["owner-operating-dashboard", context.tenantId, context.storeId, businessDate];
}

export async function getOwnerOperatingDashboard(
  businessDate: string,
  context: OwnerDashboardOperatorContext,
  signal?: AbortSignal,
): Promise<OwnerOperatingDashboardReadModel> {
  if (!isCanonicalDate(businessDate) || !operatorContextIsCanonical(context)) {
    throw new ApiRequestError(
      "REQUEST_VALIDATION_FAILED",
      "营业日期或当前门店身份不正确，系统没有读取老板经营看板",
      400,
    );
  }
  const query = new URLSearchParams({ businessDate });
  const value = await requestOperatorJson<unknown>(
    `/v1/owner/operating-dashboard?${query.toString()}`,
    {
      cache: "no-store",
      ...(signal ? { signal } : {}),
    },
  );
  if (
    !isOwnerOperatingDashboardReadModel(value, {
      tenantId: context.tenantId,
      storeId: context.storeId,
      businessDate,
    })
  ) {
    throw new ApiRequestError(
      "GATEWAY_RESPONSE_INVALID",
      "老板经营看板无法安全确认，旧数据和金额已隐藏，请重新读取",
    );
  }
  return value;
}

export function ownerDashboardDrillHref(
  target: OwnerDashboardDrillTarget,
  businessDate: string,
): string {
  if (!isCanonicalDate(businessDate)) {
    throw new ApiRequestError(
      "OWNER_DASHBOARD_DRILL_DATE_INVALID",
      "看板营业日期无法安全确认，明细入口没有打开",
    );
  }
  const href = drillHrefByTarget[target];
  if (!href) {
    throw new ApiRequestError(
      "OWNER_DASHBOARD_DRILL_TARGET_INVALID",
      "这项看板提醒的明细入口无法安全打开，请从业务菜单进入",
    );
  }
  return target === "ORDERS" ? `${href}?${new URLSearchParams({ businessDate }).toString()}` : href;
}

export function ownerDashboardStatusDisplay(
  status: OwnerDashboardStatus,
): OwnerDashboardStatusDisplay {
  return statusDisplayByStatus[status];
}

export function ownerDashboardFenLabel(value: number | null): string {
  if (value === null) return "暂不可用";
  return new Intl.NumberFormat("zh-CN", {
    style: "currency",
    currency: "CNY",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value / 100);
}

export function ownerDashboardCountLabel(value: number | null): string {
  return value === null ? "暂不可用" : `${value} 项`;
}
